// src/context/persona/personaStyles.js
//
// The inline styles of the personas screen, lifted out of PersonasView.jsx so
// the structured editor and its sub-components can share them without one
// importing the other's module graph.
//
// ⛔ Theme tokens only (`../../constants/theme.js`) — no hex, radius or
// font-family is written here by hand.

import { COLORS, FONT_SANS, RADII, SEMANTIC } from '../../constants/theme.js';

const FONT = FONT_SANS;
const ACCENT = COLORS.accent;

export const emptyBox = {
  padding: '60px 40px', textAlign: 'center', background: COLORS.surface,
  borderRadius: RADII.control, border: `1px solid ${COLORS.border}`,
  color: COLORS.textSecondary, fontSize: '13px', fontFamily: FONT,
};

/** The list's action buttons above the groups: "Suggerisci…" on the left, "Crea nuova…" pushed to the right. */
export const actionRow = {
  display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', margin: '0 0 20px',
};

export const groupHeading = {
  fontSize: '12px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
  color: COLORS.textSecondary, fontFamily: FONT, margin: '0 0 8px',
};

export const listStyle = {
  border: `1px solid ${COLORS.border}`, borderRadius: RADII.control,
  background: COLORS.surface, overflow: 'hidden',
};

/** One list row. v198: no "being edited" highlight — editing happens in the modal. */
export function rowStyle() {
  return {
    display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', fontFamily: FONT,
    borderTop: `1px solid ${COLORS.border}`, background: 'transparent',
  };
}

// The On/Off control is a TOGGLE (track + knob), not a labelled pill: the
// state is read from the knob's side and the track's fill, and the words
// ("Attiva" / "Disattiva") ride in the tooltip, never as visible text.
export function toggleTrack(enabled, disabled) {
  return {
    flexShrink: 0, position: 'relative', display: 'inline-block',
    width: '40px', height: '22px', padding: 0, boxSizing: 'border-box',
    borderRadius: RADII.pill,
    border: `1px solid ${enabled ? ACCENT : COLORS.borderStrong}`,
    background: enabled ? ACCENT : COLORS.field,
    cursor: disabled ? 'wait' : 'pointer', opacity: disabled ? 0.6 : 1,
    transition: 'background 150ms ease, border-color 150ms ease',
  };
}

export function toggleKnob(enabled) {
  return {
    position: 'absolute', top: '2px', left: enabled ? '20px' : '2px',
    width: '16px', height: '16px', borderRadius: '50%',
    background: COLORS.surface, boxShadow: `0 1px 2px ${COLORS.borderStrong}`,
    transition: 'left 150ms ease',
  };
}

export function alertBox(color) {
  return {
    marginBottom: '16px', padding: '10px 14px', borderRadius: RADII.controlInner,
    background: `${color}14`, border: `1px solid ${color}33`,
    color, fontSize: '13px', fontFamily: FONT,
  };
}

export function primaryBtn(disabled) {
  return {
    padding: '8px 16px', borderRadius: RADII.controlInner, border: 'none',
    background: disabled ? COLORS.borderStrong : ACCENT, color: COLORS.surface,
    fontSize: '13px', fontWeight: 600, fontFamily: FONT,
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
}

/** The accent's white variant: white surface, accent border and text — the same size as `primaryBtn`. */
export function accentOutlineBtn(disabled) {
  return {
    padding: '7px 15px', borderRadius: RADII.controlInner, border: `1px solid ${ACCENT}`,
    background: COLORS.surface, color: ACCENT,
    fontSize: '13px', fontWeight: 600, fontFamily: FONT,
    cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1,
  };
}

export function ghostBtn(disabled) {
  return {
    padding: '6px 12px', borderRadius: RADII.controlInner,
    border: `1px solid ${COLORS.borderStrong}`, background: COLORS.surface,
    color: COLORS.ink, fontSize: '12px', fontFamily: FONT,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
  };
}


export const textInput = {
  display: 'block', width: '100%', maxWidth: '420px', boxSizing: 'border-box',
  marginTop: '6px', padding: '8px 10px', fontSize: '14px', fontFamily: FONT,
  color: COLORS.ink, background: COLORS.surface,
  border: `1px solid ${COLORS.borderStrong}`, borderRadius: RADII.control,
};

export const fieldLabel = { display: 'block', fontFamily: FONT };

export const fieldName = {
  display: 'block', marginBottom: '6px',
  fontSize: '12px', fontWeight: 700, letterSpacing: '0.03em',
  textTransform: 'uppercase', color: COLORS.textSecondary,
};

export const sectionTextarea = (readOnly) => ({
  width: '100%', boxSizing: 'border-box', padding: '12px',
  fontFamily: FONT, fontSize: '14px', lineHeight: 1.6,
  color: COLORS.ink, background: readOnly ? COLORS.field : COLORS.surface,
  border: `1px solid ${COLORS.borderStrong}`, borderRadius: RADII.control,
  resize: 'vertical',
});

export const choiceHint = {
  display: 'block', fontSize: '12px', color: COLORS.textSecondary, marginTop: '2px',
};

// Longhand throughout: spreading `choiceHint` and then a `margin` SHORTHAND over
// its `marginTop` is exactly the collision React warns about in development.
export const pairNoteStyle = {
  ...choiceHint,
  marginTop: '10px', marginBottom: 0, marginLeft: 0, marginRight: 0,
};

// ── added for the structured editor ──────────────────────────────────────────

export const fieldError = {
  display: 'block', marginTop: '4px', fontSize: '12px',
  color: SEMANTIC.errore.fg, fontFamily: FONT,
};

/** v197: a number the AI wrote that no input contains — a note for the user, never an error. */
export const fieldWarning = {
  display: 'block', marginTop: '6px', fontSize: '12px', color: SEMANTIC.attenzione.fg, fontFamily: FONT,
};

export const itemRow = {
  display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '8px',
  alignItems: 'start', marginBottom: '8px',
};

// ── v194: the readable form ──────────────────────────────────────────────────
// Every box wraps and grows (GrowingTextarea), the sections are cards, and a
// Pain & Gain row is a numbered card of its own. Theme tokens only.

const boxBase = (readOnly, invalid) => ({
  display: 'block', width: '100%', boxSizing: 'border-box',
  marginTop: '8px', padding: '11px 14px',
  fontFamily: FONT, fontSize: '14px', lineHeight: 1.6,
  color: COLORS.ink, background: readOnly ? COLORS.field : COLORS.surface,
  border: `1px solid ${invalid ? SEMANTIC.errore.fg : COLORS.borderStrong}`,
  borderRadius: RADII.control,
});

/** A single-line value shown in a box that wraps: no truncation, no scrollbar. */
export const growInput = (readOnly, invalid) => boxBase(readOnly, invalid);

/** A paragraph field: same box, tall enough to invite more than one line. */
export const paragraphInput = (readOnly, invalid) => ({
  ...boxBase(readOnly, invalid), minHeight: '112px', lineHeight: 1.65,
});

/** The persona's name: the one bigger box at the top of the form. */
export const nameInput = (readOnly, invalid) => ({
  ...boxBase(readOnly, invalid), fontSize: '16px', fontWeight: 600, maxWidth: '560px',
});

export const sectionCard = {
  marginTop: '24px', padding: '20px 18px',
  border: `1px solid ${COLORS.border}`, borderRadius: RADII.control,
  background: COLORS.surface, fontFamily: FONT,
  display: 'flex', flexDirection: 'column', gap: '20px',
};

export const sectionTitle = {
  margin: 0, fontSize: '15px', fontWeight: 700, color: COLORS.ink, fontFamily: FONT,
};

export const sectionHint = {
  margin: '-8px 0 4px', fontSize: '12px', color: COLORS.textSecondary, lineHeight: 1.6, fontFamily: FONT,
};

/** Two paragraph fields side by side on a wide screen, stacked on a narrow one. */
export const twoColumns = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px',
};

// The cells take the WHOLE card width: the × (when there is one) is pinned in
// the top-right corner, on the line of the field labels, not in a column of its
// own beside the cells.
export const pgCard = {
  position: 'relative',
  padding: '14px 14px 16px', marginBottom: '14px', borderRadius: RADII.controlInner,
  border: `1px solid ${COLORS.border}`, background: COLORS.bg,
};

/** The row's ×, in the card corner above the Gain box (labels sit left, so it never covers one). */
export const pgRemove = { position: 'absolute', top: '8px', right: '10px' };

/** The row's two lines: Pain + Gain, then Indicatori. */
export const pgStack = { display: 'flex', flexDirection: 'column', gap: '16px' };

/** Pain and Gain: half the card each, wrapping to a stack below ~600px of card. */
export const pgPair = { display: 'flex', flexWrap: 'wrap', gap: '16px 14px' };

export const pgPairCell = { flex: '1 1 280px', minWidth: 0 };

export function iconBtn(disabled) {
  return {
    flexShrink: 0, width: '28px', height: '28px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    padding: 0, borderRadius: RADII.controlInner,
    border: `1px solid ${COLORS.borderStrong}`, background: COLORS.surface, color: COLORS.textSecondary,
    cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1,
  };
}

/** The row's edit control: a pencil icon, accent-filled while its editor is open. */
export function editIconBtn(active) {
  return {
    flexShrink: 0, width: '32px', height: '32px',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    padding: 0, borderRadius: RADII.controlInner, cursor: 'pointer',
    border: `1px solid ${active ? ACCENT : COLORS.borderStrong}`,
    background: active ? ACCENT : COLORS.surface,
    color: active ? COLORS.surface : COLORS.textSecondary,
  };
}

// ── v198: the persona modal ──────────────────────────────────────────────────
/** The footer row of a modal step: primary action(s) left, status / Chiudi right. */
export const footerRow = { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', fontFamily: FONT };

/** A wizard step's question heading ("Chi è e come gli parliamo?"). */
export const stepHeading = { margin: '0 0 16px', fontSize: '16px', fontWeight: 700, color: COLORS.ink, fontFamily: FONT };

/** A short field on ONE row: its label on the left (a fixed column, so Mercato and Nome line up), the control
 *  on the right. Wraps under the label on a narrow screen. */
export const inlineField = { display: 'flex', alignItems: 'center', gap: '8px 12px', flexWrap: 'wrap', fontFamily: FONT };
export const inlineFieldName = { ...fieldName, marginBottom: 0, flex: '0 0 110px' };

/** A value shown, not edited (the market outside create). */
export const readOnlyValue = { margin: '6px 0 0', fontSize: '14px', color: COLORS.ink, fontFamily: FONT };

/** The row holding the IT/EN switch and the Avanzate toggle. */
export const viewRow = {
  display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', margin: '20px 0 8px',
};

/** One option of the "Metodo" chooser: a large button with a title and a hint. */
export function choiceCard(recommended) {
  return {
    display: 'block', width: '100%', textAlign: 'left', padding: '16px 18px', marginBottom: '12px',
    borderRadius: RADII.control, cursor: 'pointer', fontFamily: FONT,
    border: `1px solid ${recommended ? ACCENT : COLORS.borderStrong}`,
    background: recommended ? COLORS.accentSoft : COLORS.surface, color: COLORS.ink,
  };
}
